import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Box,
    Card,
    CardContent,
    Typography,
    TextField,
    Button,
    MenuItem,
    Grid,
    Divider,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    CircularProgress
} from '@mui/material';
import { ArrowBack as BackIcon, Save as SaveIcon } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { transactionsAPI, categoriesAPI, expenseTypesAPI, membersAPI, paymentModesAPI } from '../api/api';
import { useAuth } from '../contexts/AuthContext';
import { formatCurrency, formatDateForInput, LABELS } from '../config';

const TransactionForm = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const isEdit = Boolean(id);

    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [categories, setCategories] = useState([]);
    const [expenseTypes, setExpenseTypes] = useState([]);
    const [members, setMembers] = useState([]);
    const [paymentModes, setPaymentModes] = useState([]);
    const [previewShares, setPreviewShares] = useState([]);

    const [formData, setFormData] = useState({
        transaction_date: new Date().toISOString().split('T')[0],
        particulars: '',
        amount: '',
        accounting_category_id: '',
        expense_type_id: '',
        member_id: '',
        payment_mode: 'CASH',
        remarks: ''
    });

    useEffect(() => {
        if (user && user.role === 'VIEWER') {
            toast.error('You do not have permission to add or edit transactions');
            navigate('/transactions');
            return;
        }
        loadInitialData();
    }, [id, user]);

    useEffect(() => {
        // Preview shares when amount and expense type OR investment member changes
        const selectedCat = categories.find(c => c.id === parseInt(formData.accounting_category_id));
        const isInvestmentCat = selectedCat?.type === 'INCOME_INVESTMENT';

        if (formData.amount && formData.expense_type_id) {
            previewShareCalculation();
        } else if (formData.amount && isInvestmentCat && formData.member_id) {
            // For investment, show 100% to selected member
            const selectedMember = members.find(m => m.id === parseInt(formData.member_id));
            if (selectedMember) {
                setPreviewShares([{
                    memberId: selectedMember.id,
                    memberName: selectedMember.name,
                    shareAmount: parseFloat(formData.amount),
                    sharePercentage: 100
                }]);
            }
        } else {
            setPreviewShares([]);
        }
    }, [formData.amount, formData.expense_type_id, formData.member_id, formData.accounting_category_id, categories, members]);

    const loadInitialData = async () => {
        try {
            setLoading(true);
            const [catRes, expRes, memRes, pmRes] = await Promise.all([
                categoriesAPI.getActive(),
                expenseTypesAPI.getActive(),
                membersAPI.getActive(),
                paymentModesAPI.getActive()
            ]);

            if (catRes.data.success) setCategories(catRes.data.data);
            if (expRes.data.success) setExpenseTypes(expRes.data.data);
            if (memRes.data.success) setMembers(memRes.data.data);
            if (pmRes.data.success) setPaymentModes(pmRes.data.data);

            // Load transaction if editing
            if (id) {
                const txnRes = await transactionsAPI.getById(id);
                if (txnRes.data.success) {
                    const txn = txnRes.data.data;
                    setFormData({
                        transaction_date: formatDateForInput(txn.transaction_date),
                        particulars: txn.particulars,
                        amount: txn.amount,
                        accounting_category_id: txn.accounting_category_id,
                        expense_type_id: txn.expense_type_id || '',
                        member_id: txn.member_id || '',
                        payment_mode: txn.payment_mode || 'CASH',
                        remarks: txn.remarks || ''
                    });
                    if (txn.shares) {
                        setPreviewShares(txn.shares);
                    }
                }
            }
        } catch (error) {
            console.error('Error loading data:', error);
            toast.error('Failed to load data');
        } finally {
            setLoading(false);
        }
    };

    const previewShareCalculation = async () => {
        try {
            const response = await transactionsAPI.previewShares({
                amount: parseFloat(formData.amount),
                expense_type_id: parseInt(formData.expense_type_id),
                member_id: formData.member_id ? parseInt(formData.member_id) : null
            });
            if (response.data.success) {
                setPreviewShares(response.data.data);
            }
        } catch (error) {
            console.error('Error previewing shares:', error);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.particulars || !formData.amount || !formData.accounting_category_id) {
            toast.error('Please fill in all required fields');
            return;
        }

        try {
            setSaving(true);
            const payload = {
                ...formData,
                amount: parseFloat(formData.amount),
                accounting_category_id: parseInt(formData.accounting_category_id),
                expense_type_id: formData.expense_type_id ? parseInt(formData.expense_type_id) : null,
                member_id: formData.member_id ? parseInt(formData.member_id) : null
            };

            if (isEdit) {
                await transactionsAPI.update(id, payload);
                toast.success('Transaction updated successfully');
            } else {
                await transactionsAPI.create(payload);
                toast.success('Transaction created successfully');
            }

            navigate('/transactions');
        } catch (error) {
            console.error('Error saving transaction:', error);
            toast.error('Failed to save transaction');
        } finally {
            setSaving(false);
        }
    };

    const selectedCategory = categories.find(c => c.id === parseInt(formData.accounting_category_id));
    const isExpense = selectedCategory?.type === 'EXPENSE';
    const isInvestment = selectedCategory?.type === 'INCOME_INVESTMENT';
    const selectedExpenseType = expenseTypes.find(et => et.id === parseInt(formData.expense_type_id));

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box>
            {/* Header */}
            <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
                <Button startIcon={<BackIcon />} onClick={() => navigate('/transactions')}>
                    Back
                </Button>
                <Typography variant="h5" fontWeight={600}>
                    {isEdit ? 'Edit Transaction' : 'New Transaction'}
                </Typography>
            </Box>

            <form onSubmit={handleSubmit}>
                <Grid container spacing={3}>
                    {/* Form Fields */}
                    <Grid item xs={12} md={7}>
                        <Card>
                            <CardContent>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            label="Date"
                                            type="date"
                                            name="transaction_date"
                                            value={formData.transaction_date}
                                            onChange={handleChange}
                                            fullWidth
                                            required
                                            InputLabelProps={{ shrink: true }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            label="Amount (₹)"
                                            name="amount"
                                            type="number"
                                            value={formData.amount}
                                            onChange={handleChange}
                                            fullWidth
                                            required
                                            inputProps={{ min: 0, step: 0.01 }}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextField
                                            label="Particulars / Description"
                                            name="particulars"
                                            value={formData.particulars}
                                            onChange={handleChange}
                                            fullWidth
                                            required
                                            multiline
                                            rows={2}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            label="Category"
                                            name="accounting_category_id"
                                            value={formData.accounting_category_id}
                                            onChange={handleChange}
                                            select
                                            fullWidth
                                            required
                                        >
                                            {categories.map((cat) => (
                                                <MenuItem key={cat.id} value={cat.id}>
                                                    {cat.name}
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            label="Payment Mode"
                                            name="payment_mode"
                                            value={formData.payment_mode}
                                            onChange={handleChange}
                                            select
                                            fullWidth
                                            required
                                        >
                                            {paymentModes.map((pm) => (
                                                <MenuItem key={pm.code} value={pm.code}>
                                                    {pm.name}
                                                </MenuItem>
                                            ))}
                                            {paymentModes.length === 0 && (
                                                <MenuItem value="CASH">Cash (Default)</MenuItem>
                                            )}
                                        </TextField>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        {isExpense ? (
                                            <TextField
                                                label="Division Type"
                                                name="expense_type_id"
                                                value={formData.expense_type_id}
                                                onChange={handleChange}
                                                select
                                                fullWidth
                                            >
                                                <MenuItem value="">Select Division Type</MenuItem>
                                                {expenseTypes.map((et) => (
                                                    <MenuItem key={et.id} value={et.id}>
                                                        {et.name}
                                                    </MenuItem>
                                                ))}
                                            </TextField>
                                        ) : null}
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        {(isInvestment || (isExpense && selectedExpenseType?.division_basis === 'INDIVIDUAL')) ? (
                                            <TextField
                                                label="Contributing Member"
                                                name="member_id"
                                                value={formData.member_id}
                                                onChange={handleChange}
                                                select
                                                fullWidth
                                                required
                                                helperText={isInvestment ? "Select member who made this investment" : "Select who is responsible for this individual expense"}
                                            >
                                                <MenuItem value="">Select Member</MenuItem>
                                                {members.map((m) => (
                                                    <MenuItem key={m.id} value={m.id}>
                                                        {m.name}
                                                    </MenuItem>
                                                ))}
                                            </TextField>
                                        ) : null}
                                    </Grid>
                                    {(!isExpense && !isInvestment) && (
                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                label="Division Type"
                                                name="expense_type_id"
                                                value=""
                                                select
                                                fullWidth
                                                disabled
                                                helperText="Not applicable for this category"
                                            >
                                                <MenuItem value="">N/A</MenuItem>
                                            </TextField>
                                        </Grid>
                                    )}
                                    <Grid item xs={12}>
                                        <TextField
                                            label="Remarks"
                                            name="remarks"
                                            value={formData.remarks}
                                            onChange={handleChange}
                                            fullWidth
                                            multiline
                                            rows={2}
                                        />
                                    </Grid>
                                </Grid>

                                <Divider sx={{ my: 3 }} />

                                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                                    <Button variant="outlined" onClick={() => navigate('/transactions')}>
                                        Cancel
                                    </Button>
                                    <Button
                                        type="submit"
                                        variant="contained"
                                        startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                                        disabled={saving}
                                    >
                                        {isEdit ? 'Update' : 'Save'} Transaction
                                    </Button>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Share Preview */}
                    <Grid item xs={12} md={5}>
                        <Card>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>
                                    Member Share Preview
                                </Typography>
                                {previewShares.length > 0 ? (
                                    <TableContainer component={Paper} variant="outlined">
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>Member</TableCell>
                                                    <TableCell align="right">Share %</TableCell>
                                                    <TableCell align="right">Amount</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {previewShares.map((share) => (
                                                    <TableRow key={share.memberId}>
                                                        <TableCell>{share.memberName}</TableCell>
                                                        <TableCell align="right">{(share.sharePercentage || 0).toFixed(2)}%</TableCell>
                                                        <TableCell align="right">{formatCurrency(share.shareAmount)}</TableCell>
                                                    </TableRow>
                                                ))}
                                                <TableRow>
                                                    <TableCell colSpan={2}><strong>Total</strong></TableCell>
                                                    <TableCell align="right">
                                                        <strong>
                                                            {formatCurrency(previewShares.reduce((sum, s) => sum + s.shareAmount, 0))}
                                                        </strong>
                                                    </TableCell>
                                                </TableRow>
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                ) : (
                                    <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                                        {isExpense
                                            ? (formData.expense_type_id ? 'Enter amount to see share preview' : 'Select a division type to see share breakdown')
                                            : isInvestment
                                                ? 'Select a member and enter amount to see share preview'
                                                : 'Share breakdown not applicable for this category'
                                        }
                                    </Typography>
                                )}
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            </form>
        </Box>
    );
};

export default TransactionForm;
